import { css, type SerializedStyles } from "@emotion/react";
import { forwardRef } from "react";
import {
  type AriaTextFieldOptions,
  useFocusRing,
  useTextField,
} from "react-aria";

import { cssFns } from "../../../css";
import { usePrincipalColors } from "../../../theme";
import { FieldDescription } from "../../common/description";
import { FieldError } from "../../common/error";
import { BaseFieldInput, BaseFieldLayout } from "../../common/field";
import type { Field, FieldState } from "../../common/form";
import { FieldLabel } from "../../common/label";

export type PhoneFieldState = FieldState<string>;

export type PhoneFieldInputProps = {
  readonly?: boolean;
  field: Field<PhoneFieldState>;
  icon?: React.ReactNode;
  onIconPress?: () => void;
  autoFocus?: boolean;
  clearable?: boolean;
  maxLength?: number;
  minLength?: number;
  onClearAction?: () => void;
  onChange?: () => void;
  placeholder?: string;
  label?: string;
  description?: string;
  onEnterPress?: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disableLabelFocus?: boolean;
  focusMode?: "normal" | "filled";
  styles?: SerializedStyles;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
};

export const Input = forwardRef<HTMLInputElement, PhoneFieldInputProps>(
  (
    {
      label,
      description,
      placeholder,
      field,
      icon,
      onIconPress,
      autoFocus,
      readonly,
      clearable,
      maxLength,
      minLength,
      onClearAction,
      onPaste,
      onChange,
      onEnterPress,
      onBlur,
      onFocus,
      disableLabelFocus,
      focusMode = "normal",
      styles: overrideStyles,
    },
    externalRef,
  ) => {
    const principalColors = usePrincipalColors();
    const ref = normalizeRef(externalRef);

    const { isFocused, focusProps } = useFocusRing({
      isTextInput: true,
      within: true,
    });

    const styles = css([
      cssFns.margin("0px"),
      cssFns.border({ style: "none", radius: "4px", width: "0px" }),
      cssFns.typo({ level: "body-1", weight: "regular" }),
      cssFns.padding("0px", icon ? "8px" : "10px"),
      {
        width: "100%",
        height: "100%",
        color:
          readonly || field.disabled
            ? principalColors.gs8
            : principalColors.gs2,
        backgroundColor: "transparent",
        outlineStyle: "none",
        cursor: readonly || field.disabled ? "default" : "text",
        "::placeholder": {
          color:
            readonly || field.disabled
              ? principalColors.gs8
              : principalColors.gs4,
        },
      },
    ]);

    const ariaOptions: AriaTextFieldOptions<"input"> = {
      label,
      "aria-label": label ? undefined : (placeholder ?? "phone-field"),
      description,
      isReadOnly: readonly,
      type: "tel",
      name: "phone",
      inputMode: "tel",
      isRequired: field.required,
      isDisabled: field.disabled,
      value: field.value,
      isInvalid: !!field.error?.visible,
      placeholder,
      autoFocus,
      maxLength,
      minLength,
      autoComplete: "tel",
      onChange(value: string) {
        field.onChange({ ...field, value });
        onChange?.();
      },
      onKeyDown(e) {
        if (e.key === "Enter") {
          onEnterPress?.();
        }
      },
      onBlur,
      onFocus,
    };

    const { labelProps, inputProps, descriptionProps, errorMessageProps } =
      useTextField(ariaOptions, ref);

    const handleClear = () => {
      field.onChange({ ...field, value: "" });
      onClearAction?.();
    };

    return (
      <BaseFieldLayout
        label={
          label && (
            <FieldLabel
              labelProps={labelProps}
              disabled={field.disabled}
              required={field.required}
              disableFocus={disableLabelFocus}
            >
              {label}
            </FieldLabel>
          )
        }
        input={
          <BaseFieldInput
            field={field}
            focused={isFocused}
            fieldProps={focusProps}
            input={
              <input
                {...inputProps}
                onPaste={onPaste}
                ref={ref}
                placeholder={placeholder}
                css={styles}
                readOnly={readonly}
              />
            }
            onClear={clearable && field.value ? handleClear : undefined}
            startIcon={icon}
            onStartIconPress={onIconPress}
            disabled={field.disabled}
            focusMode={focusMode}
            styles={overrideStyles}
          />
        }
        description={
          description && (
            <FieldDescription
              disabled={field.disabled}
              descriptionProps={descriptionProps}
            >
              {description}
            </FieldDescription>
          )
        }
        error={
          field.error?.visible && (
            <FieldError
              errorMessageProps={errorMessageProps}
              errorMessage={field.error.message}
              fieldRequired={field.required}
            />
          )
        }
      />
    );
  },
);

// Вот твоя чистая нормализация рефа
function normalizeRef<T>(ref: React.Ref<T> | undefined): React.RefObject<T> {
  if (ref == null) {
    return { current: null };
  }
  if (typeof ref === "function") {
    return { current: null };
  }
  return ref;
}
